import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import PremiumLink from './PremiumLink';
import { useRatingMap } from '../utils/stats';

/** 5-star row with half-step fill driven by `value` (0–5). */
function StarRow({ value = 0 }) {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    let pct = 0;
    if (value >= i) pct = 100;
    else if (value > i - 1) pct = Math.round((value - (i - 1)) * 100);
    stars.push(
      <span key={i} className="book-rating-star" aria-hidden="true">
        <span className="book-rating-star-bg">★</span>
        <span className="book-rating-star-fg" style={{ width: `${pct}%` }}>★</span>
      </span>
    );
  }
  return <span className="book-rating-stars">{stars}</span>;
}

/**
 * Shared "About this book" popup. Visually matches the premium-gate so
 * the unsubscribed flow (click Read → premium gate) feels of-a-piece.
 *
 * `book` accepts the same shape returned by /api/books OR the lighter
 * favourites/playlist projections — fields are looked up defensively so
 * a missing description/cover/title doesn't crash the modal.
 */
export default function BookDetailsModal({
  open,
  onClose,
  book,
  lang = 'en',
  readPath,
  quizPath,
  onActionClick = () => undefined,
}) {
  const [atTop, setAtTop] = useState(true);

  useEffect(() => {
    if (!open) return;
    setAtTop(true);
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  const handleScroll = (e) => {
    const top = e.currentTarget.scrollTop <= 8;
    if (top !== atTop) setAtTop(top);
  };

  // Hook is unconditional — placement above the early return keeps the
  // call order stable across renders. Cheap module-level cache.
  const ratingMap = useRatingMap();
  if (!open || !book) return null;

  const isRtl = lang === 'ar';
  const cover = (isRtl && book.coverImageAr) ? book.coverImageAr : (book.coverImage || book.cover);
  const title = (isRtl && book.titleAr) ? book.titleAr : book.title;
  const description = (isRtl && book.descriptionAr) ? book.descriptionAr : book.description;
  const author = book.author || 'Unknown';
  const ratingInfo = ratingMap.get(String(book._id));
  const showRating = book.ratingsVisible !== false && ratingInfo && ratingInfo.count > 0;

  const stop = (e) => e.stopPropagation();

  return createPortal(
    <div
      className="book-details-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={`${title} — details`}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="book-details-modal" onClick={stop}>
        <button
          type="button"
          className={`book-details-modal-close${atTop ? '' : ' is-hidden'}`}
          aria-label="Close"
          aria-hidden={!atTop}
          tabIndex={atTop ? 0 : -1}
          onClick={onClose}
        >
          ×
        </button>

        <div className="book-details-modal-scroll" onScroll={handleScroll}>
          {cover && (
            <img className="book-details-modal-cover" src={cover} alt="" />
          )}

          <div className="book-details-modal-eyebrow">About this book</div>
          <h2 className="book-details-modal-title">{title}</h2>
          <div className="book-details-modal-meta">
            {author}
            {book.ageRange ? ` · Ages ${book.ageRange}` : ''}
            {(() => {
              const list = (Array.isArray(book.genres) && book.genres.length ? book.genres
                           : book.genre ? [book.genre]
                           : Array.isArray(book.categories) && book.categories.length ? book.categories
                           : book.category ? [book.category]
                           : []);
              return list.length ? ` · ${list.join(' · ')}` : '';
            })()}
          </div>

          {showRating && (
            <div className="book-details-modal-rating" aria-label={`${ratingInfo.average} out of 5, ${ratingInfo.count} ratings`}>
              <StarRow value={ratingInfo.average} />
              <span className="book-details-modal-rating-value">{ratingInfo.average.toFixed(1)}</span>
              <span className="book-details-modal-rating-count">
                {ratingInfo.count} {ratingInfo.count === 1 ? 'rating' : 'ratings'}
              </span>
            </div>
          )}

          <p className={`book-details-modal-desc${description ? '' : ' is-empty'}`}>
            {description || 'No description has been added for this book yet.'}
          </p>

          {(readPath || quizPath) && (
            <div className="book-details-modal-actions">
              {readPath && (
                <PremiumLink
                  to={readPath}
                  className="card-action-btn card-action-read"
                >
                  Read
                </PremiumLink>
              )}
              {quizPath && (
                <PremiumLink
                  to={quizPath}
                  onClick={onActionClick(quizPath)}
                  className="card-action-btn card-action-quiz"
                >
                  Quiz
                </PremiumLink>
              )}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
